import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import moment from 'moment';
import chroma from 'chroma-js';
import ReactMarkdown from 'react-markdown';
import { countries } from '../../utils/primaryData';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useTheme } from '@mui/material/styles';
import * as Icons from '@mui/icons-material';
import { Chip, Typography, Tooltip, Switch } from '@mui/material';
import Avatar from './Avatar';
import styled from '@emotion/styled';
import GeneralSwitch from './GeneralSwitch';



export const GeneralText = ({
  primary,
  size,
  text,
  fontSize,
  style,
  label,
  markdown,
  color,
  ui,
  type,
  restrictContent,
  structureId,
  fromMain,
  maxCharacters,
  uppercase,
  icon,
  layout,
  classNameComponent,
  onClick,
  isSub,
  keyStructure,
  lineHeight,
  selections,
  lineClamp
}) => {
  const mode = primary
    ? 'storybook-text--primary'
    : 'storybook-text--secondary';

  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const currentlangCode = i18n.language;
  const currentHost = window.location.origin;

  const businessStructure = useSelector(
    (state) => state.core.businessStructure
  );
  const businessPreference = useSelector((state) => state.core.businessData);
  const businessStructures = businessStructure?.structures;
  const employees = businessPreference?.employees;
  const locations = businessPreference?.locations;

  const structure = businessStructures?.find((s) => s.id === structureId);

  const fields = structure?.fields;

  function getStatusFromPreferences(statusLabel) {
    const statusField = fields?.find((field) => field?.typeData === 'status');

    const status = statusField?.selections?.find(
      (selection) => selection?.value?.toString() === statusLabel?.toString()
    );


    return (
      status?.['label_' + currentlangCode] || status?.label || t('notAvailable')
    );
  }

  function getColorFromPreferences(statusLabel, color, type) {
    const statusField = fields?.find((field) => field.typeData === 'status');
    const status = statusField?.selections?.find(
      (selection) =>
        (
          selection?.['label_' + currentlangCode] || selection?.label
        )?.toString() === statusLabel?.toString()
    );
    const colorValue = status?.color || businessPreference?.mainColor || '#000000';
    const lightColor = chroma(color ? color === 'primary' ? businessPreference?.mainColor : color : colorValue).luminance(0.85).hex();

    return color === '#fff' ? colorValue : (lightColor + '69');
  }

  function getIconFromPreferences(statusLabel, typeData) {
    const priorityField = fields?.find((field) => field?.typeData === typeData);
    const icon = priorityField?.selections?.find(
      (selection) =>
        (
          selection?.['label_' + currentlangCode] || selection?.label
        )?.toString() === statusLabel?.toString()
    )?.icon;

    return icon;
  }

  function getEmployeeAvatar(employee) {
    const employeeData = employees?.find((emp) => emp.id === employee);
    return employeeData?.avatar;
  }

  function getSelectionsFromPreferences() {
    const priorityFieldSelections = fields?.find((field) => field?.value === keyStructure);
    const selectionsList = priorityFieldSelections?.selections
    return selectionsList?.length > 0 ? selectionsList : selections
  }

  function getEmployeeName(employee) {
    const employeeData = employees?.find((emp) => emp.id === employee);
    return (
      employeeData?.publicDisplay?.name ||
      employeeData?.displayName ||
      employeeData?.name
    );
  }

  function getLocationName(location) {
    const locationData = locations?.find((emp) => emp.id === location);
    return locationData?.name || '';
  }

  // Check if dark mode is active
  const isDarkMode = theme.palette.mode === 'dark';

  function CustomLinkRenderer(props, transformedText) {
    const { children, href } = props;
    const formattedFrom =
      props?.collection === 'employees'
        ? '#'
        : currentHost +
        '/bos/element/' +
        props?.collection +
        '/' +
        props?.structure +
        '/' +
        props?.id;
    const isExternalLink = href?.startsWith('http');
    return (
      <a
        href={href || formattedFrom}
        target={isExternalLink ? '_blank' : '_self'}
        rel={isExternalLink ? 'noopener noreferrer' : undefined}
        style={{
          color: color === 'white' ? '#FFF' : businessPreference?.mainColor,
          fontSize: fontSize || '11px',
        }}
      >
        {children}
      </a>
    );
  }

  const transformCustomText = (text) => {
    if (typeof text !== 'string') return '';

    // Replace single asterisks with two newlines
    const modifiedText = text?.replace(/(?<!\*)\*(?!\*)/g, '\n\n\n');
    return modifiedText;
  };

  const transformTextBasedOnType = (text, type) => {
    switch (type) {
      case 'money':
        return text === '-' ? '-' : `${text}`;
      case 'status':
        const status = color ? text : getStatusFromPreferences(text);
        return status;
      case 'priority':
        const priority = getStatusFromPreferences(text);
        return priority;
      case 'date':
        const dateFormated =
          typeof text === 'string' ? text : (text?.seconds || text?._seconds || text)
            ? moment
              .unix(text?.seconds || text?._seconds || text / 1000)
              .format('DD MMM YYYY')
            : t('noDate');

        return `${dateFormated}`;
      case 'date-time':
        const dateFormatedTime =
          typeof text === 'string' ? text : (text?.seconds || text?._seconds || text)
            ? moment
              .unix(text?.seconds || text?._seconds || text / 1000)
              .format('DD MMM YYYY HH:mm')
            : t('noDate');
        return `${dateFormatedTime}`;
      case 'assigned':
        const userName = text?.name;
        return `${userName}`;

      default:
        return text;
    }
  };

  const formatPhoneNumber = (number) => {
    if (!number) return '';
    const cleaned = number.replace(/\D/g, '');
    // Remove country code for display
    const country = countries.find(c => c.code === countries?.find(c => number?.startsWith(c?.dialCode))?.code);
    const countryCode = country?.dialCode.replace('+', '') || '';
    const numberWithoutCountryCode = cleaned.startsWith(countryCode)
      ? cleaned.slice(countryCode.length)
      : cleaned;

    if (numberWithoutCountryCode.length <= 3) return numberWithoutCountryCode;
    if (numberWithoutCountryCode.length <= 6) return `(${numberWithoutCountryCode.slice(0, 3)}) ${numberWithoutCountryCode.slice(3)}`;
    return `(${numberWithoutCountryCode.slice(0, 3)}) ${numberWithoutCountryCode.slice(3, 6)}-${numberWithoutCountryCode.slice(6, 10)}`;
  };

  const iconMap = getIconFromPreferences(text, type);
  const IconDynamic = Icons[iconMap] || Icons[icon] || Icons.Info;

  const renderContent = () => {
    const validText = typeof text === 'string' ? text : text?.toString();
    let transformedText =
      type === 'date'
        ? text
        : type === 'assigned'
          ? text
          : type === 'tags'
            ? text
            : type === 'date-time'
              ? text
              : transformCustomText(validText);
    transformedText = transformTextBasedOnType(
      transformedText,
      type === 'plainText' ? 'plainText' : type
    );

    if (maxCharacters && transformedText.length > maxCharacters) {
      transformedText = transformedText.substring(0, maxCharacters) + '...';
    }

    if (markdown) {
      return (
        <div
          className="markdown-content"
          style={{
            color: primary
              ? color === 'white'
                ? '#FFF'
                : isDarkMode
                  ? '#FFF'
                  : '#000'
              : color === 'white'
                ? '#FFF'
                : color || '#000',
            fontSize: `${fontSize}`,
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            children={transformedText}
            components={{
              a: ({ node, ...props }) => (
                <CustomLinkRenderer {...props} transformedText={props.href} />
              ),
              b: ({ node, ...props }) => (
                <span style={{ fontWeight: 600, fontSize: fontSize }}>
                  {props.children}
                </span>
              ),
              strong: ({ node, ...props }) => (
                <span style={{ fontWeight: 600, fontSize: fontSize }}>
                  {props.children}
                </span>
              ),
              ul: ({ node, ...props }) => (
                <ul
                  style={{
                    listStyleType: 'disc',
                    marginLeft: '0px',
                    fontSize: fontSize,
                  }}
                >
                  {props.children}
                </ul>
              ),
              li: ({ node, ...props }) => (
                <li
                  style={{
                    marginLeft: '-20px',
                    paddingLeft: '0em',
                    fontSize: fontSize,
                  }}
                >
                  {props.children}
                </li>
              ),
              i: ({ node, ...props }) => (
                <span style={{ fontStyle: 'italic', fontSize: fontSize }}>
                  {props.children}
                </span>
              ),
              em: ({ node, ...props }) => (
                <span style={{ fontStyle: 'italic', fontSize: fontSize }}>
                  {props.children}
                </span>
              ),
              p: ({ node, ...props }) => (
                <span style={{ margin: '4px 0', fontSize: fontSize }}>
                  {props.children}
                </span>
              ),
              br: ({ node, ...props }) => <br />,
              img: ({ node, ...props }) => (
                <img {...props} style={{ maxWidth: '100%', height: 'auto' }} />
              ),
            }}
          />
        </div>
      );
    } else if (type === 'status' || ui === 'chip') {
      return (
        <Chip
          size="xsm"
          label={transformedText}
          className={
            color !== 'white'
              ? 'smallChipText middle-content'
              : ' middle-content'
          }
          icon={type === 'status' && <IconDynamic color={color || '#000'} />}
          sx={{
            backgroundColor:
              type === 'status'
                ? getColorFromPreferences(
                  transformedText,
                  color === 'white' ? 'transparent' : color
                )
                : businessPreference?.mainColor + '10',
            color: color === 'white' ? '#FFF' : color || '#000',
            fontWeight: color === 'white' ? 600 : 400,
            height: color === 'white' ? fontSize : '22px',
            marginTop: color === 'white' ? '0px' : fromMain ? '10px' : '',
            marginBottom: color === 'white' ? '0px' : fromMain ? '0px' : '5px',
            fontSize: color === 'white' ? fontSize : '9px !important',
          }}
        />
      );
    } else if (type === 'boolean') {
      return (
        <GeneralSwitch
          checked={transformedText === 'true'}
          primary={primary}
          isDarkMode={isDarkMode}
          businessPreference={businessPreference}
        />
      );
    } else if (type === 'tags') {
      let tags = [];
      if (transformedText?.length > 0) {
        tags = transformedText;
      } else {
        tags = [t('noTag'), t('...')];
      }

      const firstTags = restrictContent ? tags?.slice(0, 2) : tags;

      // Ensure firstTags is always an array
      const safeFirstTags = Array.isArray(firstTags) ? firstTags : [];

      return (
        <div>
          {safeFirstTags.map((tag, index) => (
            <Chip
              size="small"
              key={index}
              label={tag}
              className="smallChipTextTags"
              style={{
                backgroundColor: businessPreference?.secColor + '10',
                marginBottom: '5px',
                borderRadius: '7px',
                height: '18px',
                marginRight: '3px',
              }}
            />
          ))}
        </div>
      );
    } else if (keyStructure === 'locationId') {
      return (
        <div
          className="d-flex middle-content"
        >
          <div>
            <Icons.PinDropOutlined htmlColor={
              layout?.color === 'secColor'
                ? businessStructure?.secColor
                : color || '#1a1a1a'
            } sx={{ fontSize: fontSize || '12px' }} />
          </div>
          <div
            style={{
              marginLeft: '3px',
              fontSize: fontSize || '12px'
            }}
          >
            {getLocationName(text)}
          </div>
        </div>
      );
    } else if (
      type === 'assigned' ||
      type === 'assignedToId' ||
      type === 'assignedTo'
    ) {
      return (
        <div
          style={{
            marginTop: fromMain ? '18px' : '',
            marginBottom: fromMain ? '' : '-15px',
          }}
          className="d-flex middle-content"
        >
          <div style={{ minHeight: 40 }}>
            <Avatar
              size="xsm"
              img={getEmployeeAvatar(text)}
              alt={getEmployeeName(text)}
              userId={text}
            />
          </div>
          <div
            style={{
              marginTop: '-5px',
              marginLeft: '-3px',
            }}
            className="fs-11"
          >
            {getEmployeeName(text)}
          </div>
        </div>
      );
    } else if (type === 'slider') {
      const selections = getSelectionsFromPreferences();
      const value = selections?.findIndex(s => s?.['label_' + currentlangCode] === transformedText);
      const fontSizeNum = parseInt(fontSize || '8px');

      const generateGradient = (selections) => {
        if (!selections?.length) return 'linear-gradient(to right, #696969, #696969)';
        const step = 100 / (selections.length - 1);
        let gradient = 'linear-gradient(to right, ';

        selections.forEach((selection, index) => {
          gradient += `${selection?.color || '#696969'} ${step * index}%, `;
        });

        gradient = gradient.slice(0, -2);
        gradient += ')';
        return gradient;
      };

      const StyledSliderIndicator = styled('div')({
        position: 'relative',
        width: '130px',
        height: `${fontSizeNum}px`,
        background: generateGradient(selections),
        borderRadius: '12px',
        marginTop: '4px',
        '& .icon-position': {
          position: 'absolute',
          top: `-${fontSizeNum / 2}px`,
          left: value === 0 ? fontSize : `${(value / (selections?.length - 1)) * 100}%`,
          transform: 'translateX(-50%)',
          fontSize: `${fontSizeNum * 2}px`,
          color: selections[value]?.color || '#696969',
          backgroundColor: 'white',
          border: '0.5px solid',
          borderColor: isDarkMode ? '#000' : '#FFF',
          borderRadius: '50%',
          boxShadow: '0px 0px 0px 1px #69696910',
        }
      });

      return (
        <div style={{ paddingTop: '3px' }}>
          <div>
            <StyledSliderIndicator>
              <IconDynamic className="icon-position" />
            </StyledSliderIndicator>
            <div style={{
              fontSize: '8px',
              color: isDarkMode ? '#FFFFFF90' : '#00000090',
              marginTop: '4px',
              textAlign: 'center'
            }}>
              {selections[value]?.['label_' + currentlangCode] || transformedText}
            </div>
          </div>
        </div>
      );
    } else if (type === 'selection') {
      return (
        <div className="d-flex middle-content">
          <IconDynamic
            htmlColor={color}
            selected={false}
            style={{ fontSize: fontSize || '11px' }}
          />
          <div style={{ fontSize: fontSize || '10px' }} className="px-2">
            {transformedText}
          </div>
        </div>
      );
    } else if (type === 'signature') {
      return (
        <div className="d-flex middle-content">
          {transformedText?.startsWith('http') ? (
            <img src={transformedText} style={{ height: '60px' }} alt="signature" />
          ) : (
            <img src={'/assets/v3/components/signaturePH.png'} style={{ height: '40px', marginBottom: '4px' }} alt="signature" />
          )}
        </div>
      );
    } else if (type === 'date' || type === 'date-time') {
      return (
        <div className="d-flex middle-content">
          <div>
            <Icons.CalendarMonthOutlined
              htmlColor={
                layout?.color === 'secColor'
                  ? businessStructure?.secColor
                  : color || '#1a1a1a'
              }
              sx={{
                fontSize: fontSize || '10px',
              }}
            />
          </div>
          <div className="px-2">
            <span style={{ fontSize: fontSize || '10px' }}>
              {transformedText === 'Invalid date' ? t('noDate') : transformedText}
            </span>
          </div>
        </div>
      );
    } else if (type === 'geo') {
      return (
        <div className="d-flex middle-content">
          <div>
            <Icons.FmdGoodOutlined
              htmlColor={
                layout?.color === 'secColor'
                  ? businessStructure?.secColor
                  : color || '#1a1a1a'
              }
              sx={{
                fontSize: fontSize || '10px',
              }}
            />
          </div>
          <div className="px-2">
            <span style={{ fontSize: fontSize || '10px' }}>
              {transformedText}
            </span>
          </div>
        </div>
      );
    } else if (type === 'phone') {

      return (
        <div className="d-flex middle-content">
          <div style={{ cursor: 'pointer' }} onClick={() => window.open(`tel:${transformedText}`)}>
            {transformedText?.startsWith('+') ? countries?.find(c => c.dialCode?.startsWith(transformedText?.slice(0, 2)))?.flag : (businessPreference?.country ? countries?.find(c => c.code === businessPreference?.country)?.flag : '🇨🇦')}
          </div>
          <div className="px-2">
            <span style={{ fontSize: fontSize || '10px' }}>
              {transformedText?.startsWith('+') ? formatPhoneNumber(transformedText) : transformedText}
            </span>
          </div>
        </div>
      );
    } else if (type === 'email') {
      return (
        <div className="d-flex middle-content">
          <div style={{ cursor: 'pointer' }} onClick={() => window.open(`mailto:${transformedText}`)}>
            <Icons.MailOutlined
              htmlColor={
                layout?.color === 'secColor'
                  ? businessStructure?.secColor
                  : color || '#1a1a1a'
              }
              sx={{
                fontSize: fontSize || '10px',
                marginTop: '3px',
              }}
            />
          </div>
          <div className="px-2">
            <span style={{ fontSize: fontSize || '10px' }}>
              {transformedText}
            </span>
          </div>
        </div>
      );
    } else if (type === 'dropdown') {
      return (
        <div className="d-flex middle-content">
          <div className="px-2">
            <span style={{ fontSize: fontSize || '10px' }}>
              {transformedText}
            </span>
          </div>
        </div>
      );
    } else if (type === 'list') {
      if (!Array.isArray(text) || text.length === 0) return null;

      // Get all unique keys from the objects in the array
      const allKeys = [...new Set(text.flatMap(item =>
        typeof item === 'object' && item !== null ? Object.keys(item) : []
      ))];

      // Map keys to selection labels if available
      const getHeaderLabel = (key) => {
        if (selections) {
          return selections[Number(key)].label || selections[Number(key)].label_fr || '';
        }
        return key;
      };

      return (
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: fontSize || '12px',
            marginBottom: '5px'
          }}>
            <thead>
              <tr>
                {allKeys?.map(key => (
                  <th key={key} style={{
                    padding: '0px',
                    textAlign: 'left',
                    borderBottom: '1px solid #ddd',
                    fontWeight: 500,
                    fontSize: '10px',
                    paddingRight: '14px'
                  }}>
                    {getHeaderLabel(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody >
              {Array.isArray(text) && text?.map((item, index) => (
                <tr key={index}>
                  {allKeys?.map(key => (
                    <td key={key} style={{
                      paddingRight: '14px',
                      paddingTop: '6px',
                      paddingBottom: '6px',
                      fontSize: fontSize || '10px',
                      borderBottom: '1px solid',
                      borderColor: businessPreference?.mainColor + '50',
                      color: primary ? (isDarkMode ? '#FFF' : '#000') : color || '#000'
                    }}>
                      <span style={{ fontSize: fontSize || '10px' }}>
                        {typeof item === 'object' && item !== null ? item[key] || '-' : item}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } else if (type === 'array') {
      return (
        <div style={{ marginTop: '2px', width: '100%' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {text?.map((item, rowIndex) => (
                <tr key={rowIndex}>
                  {/* Dynamically generate table data cells based on object values */}
                  {Object.keys(item).map((key, colIndex) => (
                    <td
                      key={colIndex}
                      style={{
                        borderBottom: '1px solid grey',
                        padding: '8px',
                        fontSize: fontSize || '10px',
                      }}
                    >
                      {typeof item[key] === 'object' && item[key] !== null ? (
                        // Check if item[key] is an array, then map through it
                        Array.isArray(item[key]) ? (
                          item[key]?.map((subItem, subIndex) => (
                            <div key={subIndex}>
                              {/* Render nested object properties */}
                              {Object.keys(subItem).map(
                                (subKey, subColIndex) => (
                                  <div key={subColIndex}>
                                    {subKey}: {subItem[subKey]}
                                  </div>
                                )
                              )}
                            </div>
                          ))
                        ) : (
                          // If it's an object but not an array, render its properties
                          <div>
                            {Object.keys(item[key]).map(
                              (subKey, subColIndex) => (
                                <div className="fs-10" key={subColIndex}>
                                  {subKey}: {item[key][subKey]}
                                </div>
                              )
                            )}
                          </div>
                        )
                      ) : (
                        // Render the value if it's not an object
                        item[key]
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } else {
      return transformedText
    }
  };

  return (
    <>
      <div>
        <div
          style={{
            ...style,
            fontSize: `${fontSize}`,
            color:
              color === 'white'
                ? '#FFF'
                : primary
                  ? isDarkMode
                    ? '#FFF'
                    : '#000'
                  : color || '#FFF',
            textTransform: `${uppercase ? 'uppercase' : 'none'}`,
            cursor: onClick ? 'pointer' : 'default',
            maxHeight:
              ((type === 'string' || type === 'text') && lineHeight) || null,
            lineHeight:
              ((type === 'string' || type === 'text') && lineHeight) || 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'normal',
            display: '-webkit-box',
            WebkitLineClamp: lineClamp ? lineClamp : null,
            WebkitBoxOrient: 'vertical',
          }}
          className={[
            'storybook-text',
            `storybook-text--${size}`,
            `${classNameComponent}`
          ].join(' ')}
          onClick={onClick}
          title={text}
        >

          {renderContent()}
          {businessPreference?.preferences?.fieldsView !== 'reverse' &&
            label && (
              <div style={{ marginBottom: '2px', marginTop: '-2px', }}>
                <Typography
                  variant="caption"
                  fontWeight={400}
                  color={
                    color === 'white'
                      ? '#FFF'
                      : isDarkMode
                        ? '#FFFFFF70'
                        : '#1a1a1a90'
                  }
                  fontSize={isSub ? 8 : 10}
                  sx={{

                    marginLeft:
                      color === 'white' && type === 'status' ? '23px' : '0px',
                  }}
                >
                  {label +
                    (businessPreference?.preferences?.devMode
                      ? ' - ' + keyStructure
                      : '')}
                </Typography>
              </div>
            )}
        </div>
      </div>
    </>
  );
};

export default GeneralText;